import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { ServiceType } from '@shared/providers/theme/types';
import { useAuth } from '@shared/providers/AuthContext';
import { getLatestEditingPatientId } from '@shared/lib/latestEditingPatient';
import ServiceTypeSelector from '@/components/molecules/ServiceTypeSelector';
import { getPathAfterServiceSwitch } from '@/constants/routeService';
import { getClinicMenu } from './menu/registry';
import { isMenuItem, type MenuGroup, type MenuItem } from './menu/types';

const SIDEBAR_OPEN_STORAGE_KEY = 'ra2n.console.sidebarOpen';

/** Read last sidebar open state from localStorage (default: open). */
export function readPersistedSidebarOpen(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
    if (stored === null) return true;
    return stored === 'true';
  } catch {
    return true;
  }
}

export function persistSidebarOpen(open: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, String(open));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Sidebar Props
 */
interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

/**
 * Accordion Menu Group Component
 */
const MenuGroupComponent: React.FC<{
  group: MenuGroup;
  tenant: string;
  clinicNumber?: string;
  isExpanded: boolean;
  onToggle: () => void;
  colors: {
    menuOpen: string;
    menuHover: string;
    menuActive: string;
    sidebarText: string;
  };
}> = ({ group, tenant, clinicNumber, isExpanded, onToggle, colors }) => {
  const location = useLocation();

  const normalizePath = (p: string) => p.replace(/\/$/, '') || '/';
  const isMenuItemActive = (menuPath: string, fullPath: string) => {
    const currentPath = normalizePath(location.pathname);
    const targetPath = normalizePath(fullPath);

    if (currentPath === targetPath) {
      return true;
    }

    const tenantPrefix = tenant ? `/${tenant}` : '';

    if (menuPath === '/visit_clinic/schedule') {
      return (
        currentPath === `${tenantPrefix}/visit_clinic/schedule` ||
        currentPath.startsWith(`${tenantPrefix}/visit_clinic/schedule/`) ||
        currentPath.startsWith(`${tenantPrefix}/visit_clinic/edit/`)
      );
    }

    if (menuPath === '/visits/schedule') {
      return (
        currentPath === `${tenantPrefix}/visits/schedule` ||
        currentPath.startsWith(`${tenantPrefix}/visits/schedule/`)
      );
    }

    return false;
  };
  const getResolvedPath = (path: string) => {
    if (path.startsWith('http')) {
      return path;
    }

    if (path === '/patients/edit') {
      const latestPatientId = getLatestEditingPatientId(tenant, clinicNumber);
      if (latestPatientId) {
        return `/${tenant}/patients/edit/${latestPatientId}`;
      }
    }
    if (path === '/visit_clinic/schedule') {
      const latestPatientId = getLatestEditingPatientId(tenant, clinicNumber);
      if (latestPatientId) {
        return `/${tenant}/visit_clinic/schedule?patient_id=${latestPatientId}`;
      }
    }

    return `/${tenant}${path}`;
  };

  // Flat menu link (no group): single link with icon + label
  if (group.path) {
    const fullPath = getResolvedPath(group.path);
    const isActive = isMenuItemActive(group.path, fullPath);
    return (
      <div className="border-b border-white/20">
        <Link
          to={fullPath}
          className="flex items-center w-full gap-2 px-4 py-3 transition-colors"
          style={{
            color: colors.sidebarText,
            backgroundColor: isActive ? colors.menuActive : undefined,
          }}
          onMouseEnter={e => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = colors.menuHover;
            }
          }}
          onMouseLeave={e => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          <span>{group.icon}</span>
          <span className="font-medium">{group.label}</span>
        </Link>
      </div>
    );
  }

  // Accordion group with items
  return (
    <div className="border-b border-white/20">
      <button
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="flex items-center justify-between w-full px-4 py-3 text-left transition-colors"
        style={{ color: colors.sidebarText }}
      >
        <span className="flex items-center gap-2">
          <span>{group.icon}</span>
          <span className="font-medium">{group.label}</span>
        </span>
        <span
          className={`inline-flex transform items-center transition-transform duration-200 ease-in-out ${isExpanded ? 'rotate-180' : ''}`}
        >
          <i className="text-lg fa fa-angle-down" aria-hidden="true" />
        </span>
      </button>
      {group.items && (
        <div
          aria-hidden={!isExpanded}
          className={`grid transition-[grid-template-rows,opacity] duration-200 ease-in-out ${
            isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div
            className={`min-h-0 overflow-hidden ${isExpanded ? '' : 'pointer-events-none'}`}
            style={{ backgroundColor: colors.menuOpen }}
          >
            {group.items.filter(isMenuItem).map((item: MenuItem, index: number) => {
              const fullPath = getResolvedPath(item.path);
              const isActive = isMenuItemActive(item.path, fullPath);
              // Prefer openInNewTab, but also keep support for legacy externalUrl
              if (item.openInNewTab || item.externalUrl) {
                return (
                  <Link
                    key={index}
                    to={item.externalUrl ? item.externalUrl : fullPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={isExpanded ? undefined : -1}
                    className="block px-8 py-2 text-sm transition-colors"
                    style={{
                      color: colors.sidebarText,
                      backgroundColor: isActive ? colors.menuActive : undefined,
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = colors.menuHover;
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {item.label}
                  </Link>
                );
              }

              return (
                <Link
                  key={index}
                  to={fullPath}
                  tabIndex={isExpanded ? undefined : -1}
                  className="block px-8 py-2 text-sm transition-colors"
                  style={{
                    color: colors.sidebarText,
                    backgroundColor: isActive ? colors.menuActive : undefined,
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = colors.menuHover;
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Sidebar Component
 *
 * Collapsible sidebar menu matching RA2 design with theme support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { tenant } = useParams<{ tenant: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceType, theme, colors, themeConfig, setServiceType, canSwitchServiceType } =
    useTheme();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const { clinic } = useAuth();
  const familySiteType = clinic?.parent_site_type || clinic?.site_type || '';

  // Menu comes from the registry: tenant override (customize/) when one exists,
  // otherwise the default menu for this service type. Family visibility is
  // already applied -- this component only renders.
  const menuGroups = React.useMemo(
    () =>
      getClinicMenu({
        siteType: clinic?.site_type ?? tenant,
        serviceType,
        family: familySiteType,
      }).tabs.flatMap(t => t.groups),
    [clinic?.site_type, tenant, serviceType, familySiteType]
  );

  const normalizePath = (p: string) => p.replace(/\/$/, '') || '/';
  const currentPath = normalizePath(location.pathname);
  const tenantPrefix = tenant ? `/${tenant}` : '';
  const isTenantRootPath = tenantPrefix ? currentPath === tenantPrefix : currentPath === '/';
  const activeGroupIndex = menuGroups.findIndex(group => {
    if (group.path) {
      const groupPath = normalizePath(`${tenantPrefix}${group.path}`);
      return currentPath === groupPath || currentPath.startsWith(`${groupPath}/`);
    }
    return group.items?.filter(isMenuItem).some(item => {
      if (item.path.startsWith('http')) return false;
      const itemPath = normalizePath(`${tenantPrefix}${item.path}`);
      if (currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)) return true;
      if (item.path === '/visit_clinic/schedule') {
        return (
          currentPath.startsWith(`${tenantPrefix}/visit_clinic/schedule/`) ||
          currentPath.startsWith(`${tenantPrefix}/visit_clinic/edit/`)
        );
      }
      if (item.path === '/visits/schedule') {
        return currentPath.startsWith(`${tenantPrefix}/visits/schedule/`);
      }
      return false;
    });
  });
  const autoExpandGroupIndex = activeGroupIndex >= 0 ? activeGroupIndex : isTenantRootPath ? 0 : -1;

  useEffect(() => {
    if (autoExpandGroupIndex < 0 || autoExpandGroupIndex >= menuGroups.length) return;
    setExpandedGroups(prev => {
      if (prev.has(autoExpandGroupIndex)) return prev;
      const next = new Set(prev);
      next.add(autoExpandGroupIndex);
      return next;
    });
  }, [autoExpandGroupIndex, menuGroups.length]);

  const toggleGroup = (index: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleServiceTypeChange = (type: ServiceType) => {
    if (type === serviceType) {
      return;
    }

    const nextPath = getPathAfterServiceSwitch(location.pathname, serviceType as 'A2' | 'R2');
    setServiceType(type);
    setExpandedGroups(new Set([0]));
    if (nextPath !== location.pathname) {
      navigate(nextPath);
    }
  };

  // Get colors for A2 and R2 buttons from site config
  const a2Colors = themeConfig.A2.colors;
  const r2Colors = themeConfig.R2.colors;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] ${theme.sidebarWidth} shadow-lg z-50 transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: colors.sidebarBg }}
      >
        {/* Menu Groups - Scrollable area */}
        <nav className="app-sidebar-scrollbar flex-1 overflow-y-auto py-2">
          {menuGroups.map((group, index) => (
            <MenuGroupComponent
              key={`${serviceType}-${index}`}
              group={group}
              tenant={tenant || ''}
              clinicNumber={clinic?.clinic_number}
              isExpanded={expandedGroups.has(index)}
              onToggle={() => toggleGroup(index)}
              colors={{
                menuOpen: colors.menuOpen,
                menuHover: colors.menuHover,
                menuActive: colors.menuActive,
                sidebarText: colors.sidebarText,
              }}
            />
          ))}
        </nav>

        {/* Service Type Selector (Bottom) - Fixed at bottom, only shown if clinic can use both services */}
        {canSwitchServiceType && (
          <ServiceTypeSelector
            variant="sidebar"
            currentType={serviceType}
            onTypeChange={handleServiceTypeChange}
            canSwitch={canSwitchServiceType}
            a2Colors={a2Colors}
            r2Colors={r2Colors}
            themeConfig={themeConfig}
          />
        )}
      </aside>
    </>
  );
};

export default Sidebar;
